import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'receevit_cookie_consent_v1';
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false
};

const CookieConsentContext = createContext({
  consent: DEFAULT_CONSENT,
  isBannerVisible: false,
  acceptAnalytics: () => {},
  declineAnalytics: () => {},
  openPreferences: () => {},
  setBannerVisible: () => {}
});

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [isBannerVisible, setBannerVisible] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConsent({
          necessary: true,
          analytics: Boolean(parsed.analytics)
        });
        setBannerVisible(false);
      }
    } catch (error) {
      console.warn('CookieConsent: unable to parse stored consent', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  const persistConsent = useCallback((nextConsent) => {
    setConsent(nextConsent);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConsent));
    } catch (error) {
      console.warn('CookieConsent: unable to persist consent', error);
    }
  }, []);

  const acceptAnalytics = useCallback(() => {
    persistConsent({ necessary: true, analytics: true });
    setBannerVisible(false);
  }, [persistConsent]);

  const declineAnalytics = useCallback(() => {
    persistConsent({ necessary: true, analytics: false });
    setBannerVisible(false);
  }, [persistConsent]);

  const openPreferences = useCallback(() => {
    setBannerVisible(true);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      isBannerVisible: isLoaded && isBannerVisible,
      acceptAnalytics,
      declineAnalytics,
      openPreferences,
      setBannerVisible
    }),
    [consent, isBannerVisible, isLoaded, acceptAnalytics, declineAnalytics, openPreferences]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = () => useContext(CookieConsentContext);

